/**
 * Cold query mode: every StarGrid API call asks the server to bypass the StarRocks caches, so the page
 * shows what a first-ever read costs. Scoped to the page it was switched on for (deck and filter changes
 * there stay cold); opening any other page returns to warm queries. Held in session storage, so it
 * survives the reload that applies it but never outlives the tab.
 */

const STORAGE_KEY = 'stargrid.bypassCache';
export const BYPASS_CACHE_HEADER = 'X-StarGrid-Bypass-Cache';

// The page part of the hash: a report's own query string (deck, filters) does not make it another page.
const currentPage = (): string => window.location.hash.split('?')[0];

const storedPage = (): string | null => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const isColdQueryMode = (): boolean => storedPage() === currentPage();

// Storage can throw (private mode, blocked cookies); the mode is best-effort, so swallow write failures.
const writeStoredPage = (page: string | null): void => {
  try {
    if (page === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, page);
  } catch {
    // ignore: cold query mode simply stays off when storage is unavailable
  }
};

// Leaving the page ends the mode. Cleared on navigation, not in the read, which runs during render.
const endIfLeft = (): void => {
  const page = storedPage();
  if (page !== null && page !== currentPage()) writeStoredPage(null);
};
endIfLeft();
window.addEventListener('hashchange', endIfLeft);

/** Switches the mode for the current page and reloads, so every request on it runs under the mode from the start. */
export const setColdQueryMode = (on: boolean): void => {
  writeStoredPage(on ? currentPage() : null);
  window.location.reload();
};
